/** Identyfikatory szybkich presetów dat (mapa). */
export const MAP_DATE_PRESET_IDS = ['today', 'tomorrow', 'this_week', 'this_month'];

function formatLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Zakres dat dla presetu (lokalna strefa, tydzień = pon–niedz.).
 * @param {string} preset
 * @returns {({ from: string, to: string }) | null}
 */
export function getMapDatePresetRange(preset) {
  if (!preset || !MAP_DATE_PRESET_IDS.includes(preset)) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (preset === 'today') {
    const y = formatLocalYmd(today);
    return { from: y, to: y };
  }

  if (preset === 'tomorrow') {
    const t = new Date(today);
    t.setDate(t.getDate() + 1);
    const y = formatLocalYmd(t);
    return { from: y, to: y };
  }

  if (preset === 'this_week') {
    const start = new Date(today);
    const dow = start.getDay();
    const diffMon = dow === 0 ? -6 : 1 - dow;
    start.setDate(start.getDate() + diffMon);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { from: formatLocalYmd(start), to: formatLocalYmd(end) };
  }

  if (preset === 'this_month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: formatLocalYmd(start), to: formatLocalYmd(end) };
  }

  return null;
}

/**
 * Wspólna logika filtrowania wydarzeń (mapa / lista).
 * @param {object[]} eventsToFilter
 * @param {object} filters
 * @param {{ friendIds?: Set<string>, currentUserId?: string | null }} [ctx]
 * @returns {object[]}
 */
export function applyDiscoverEventFilters(eventsToFilter, filters, ctx = {}) {
  if (!eventsToFilter || eventsToFilter.length === 0) {
    return [];
  }

  const friendIds = ctx.friendIds instanceof Set ? ctx.friendIds : null;
  const currentUserId = ctx.currentUserId || null;

  let filtered = [...eventsToFilter];

  let effectiveDateFrom = filters.dateFrom || '';
  let effectiveDateTo = filters.dateTo || '';
  if (filters.datePreset && MAP_DATE_PRESET_IDS.includes(filters.datePreset)) {
    const bounds = getMapDatePresetRange(filters.datePreset);
    if (bounds) {
      effectiveDateFrom = bounds.from;
      effectiveDateTo = bounds.to;
    }
  }

  if (effectiveDateFrom) {
    filtered = filtered.filter((event) => {
      const eventDate = new Date(`${event.event_date}T00:00:00`);
      const filterDate = new Date(`${effectiveDateFrom}T00:00:00`);
      return eventDate >= filterDate;
    });
  }

  if (effectiveDateTo) {
    filtered = filtered.filter((event) => {
      const eventDate = new Date(`${event.event_date}T00:00:00`);
      const filterDate = new Date(`${effectiveDateTo}T23:59:59.999`);
      return eventDate <= filterDate;
    });
  }

  if (filters.isFree === true) {
    filtered = filtered.filter((e) => e.is_free === true);
  } else if (filters.isFree === false) {
    filtered = filtered.filter((e) => e.is_free === false);
  }

  const rawMin = filters.priceMin != null && filters.priceMin !== '' ? Number(filters.priceMin) : NaN;
  const rawMax = filters.priceMax != null && filters.priceMax !== '' ? Number(filters.priceMax) : NaN;
  const priceMin = Number.isFinite(rawMin) ? rawMin : null;
  const priceMax = Number.isFinite(rawMax) ? rawMax : null;

  if (Number.isFinite(priceMin) || Number.isFinite(priceMax)) {
    filtered = filtered.filter((event) => {
      const perPerson = perPersonPricePln(event);
      if (perPerson == null) return false;
      if (Number.isFinite(priceMin) && perPerson < priceMin) return false;
      if (Number.isFinite(priceMax) && perPerson > priceMax) return false;
      return true;
    });
  }

  if (filters.minParticipants) {
    const min = parseInt(String(filters.minParticipants), 10);
    if (!Number.isNaN(min)) {
      filtered = filtered.filter((event) => (event.max_participants || 0) >= min);
    }
  }

  if (filters.maxParticipants) {
    const max = parseInt(String(filters.maxParticipants), 10);
    if (!Number.isNaN(max)) {
      filtered = filtered.filter((event) => (event.max_participants || 0) <= max);
    }
  }

  if (filters.friendsOnly && friendIds && friendIds.size > 0) {
    filtered = filtered.filter((event) => event.host_id && friendIds.has(event.host_id));
  }

  if (filters.mineOnly && currentUserId) {
    filtered = filtered.filter((event) => event.host_id === currentUserId);
  }

  return filtered;
}

/** Cena za osobę w PLN (płatne); bezpłatne → 0; brak danych → null */
export function perPersonPricePln(event) {
  if (event?.is_free === true) return 0;
  const total = event?.total_cost != null ? parseFloat(String(event.total_cost)) : NaN;
  const maxP = event?.max_participants != null ? Number(event.max_participants) : 0;
  if (!Number.isFinite(total) || maxP <= 0) return null;
  return total / maxP;
}

export const defaultMapDiscoverFilters = () => ({
  dateFrom: '',
  dateTo: '',
  datePreset: '',
  priceMin: '',
  priceMax: '',
  isFree: null,
  minParticipants: '',
  maxParticipants: '',
  friendsOnly: false,
  mineOnly: false,
});

export function mapDiscoverFiltersActive(f) {
  if (!f) return false;
  const quickDatePreset = f.datePreset && MAP_DATE_PRESET_IDS.includes(f.datePreset);
  const manualDateRange =
    (f.dateFrom || f.dateTo) && (!f.datePreset || f.datePreset === 'custom');
  return Boolean(
    quickDatePreset ||
      manualDateRange ||
      f.priceMin !== '' ||
      f.priceMax !== '' ||
      f.isFree === true ||
      f.isFree === false ||
      f.minParticipants ||
      f.maxParticipants ||
      f.friendsOnly ||
      f.mineOnly
  );
}

/**
 * Zwraca nowy obiekt filtrów po usunięciu pojedynczego chipa aktywnego filtra.
 * @param {object} filters
 * @param {string} chipId
 * @returns {object}
 */
export function dismissDiscoverFilterChip(filters, chipId) {
  const next = { ...defaultMapDiscoverFilters(), ...(filters || {}) };

  switch (chipId) {
    case 'datePreset':
    case 'dateFrom':
    case 'dateTo':
      next.datePreset = '';
      next.dateFrom = '';
      next.dateTo = '';
      break;
    case 'price':
      next.priceMin = '';
      next.priceMax = '';
      break;
    case 'pricing':
      next.isFree = null;
      break;
    case 'participantsMin':
      next.minParticipants = '';
      break;
    case 'participantsMax':
      next.maxParticipants = '';
      break;
    case 'friends':
      next.friendsOnly = false;
      break;
    case 'mine':
      next.mineOnly = false;
      break;
    default:
      break;
  }

  return next;
}
